import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, FileText, File, Image as ImageIcon, Film, Music, Archive, AlertTriangle } from 'lucide-react';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

const AttachmentPreview = ({ attachment, isOpen, onClose }) => {
  const [previewType, setPreviewType] = useState('none');
  const [error, setError] = useState(false);
  const [docxHtml, setDocxHtml] = useState('');
  const [xlsxHtml, setXlsxHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null); // For authenticated media loading

  // Helper to get auth token
  const getAuthToken = () => {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  };

  // Helper to make authenticated fetch
  const fetchWithAuth = async (url) => {
    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return fetch(url, { headers });
  };

  // Function to load media (images, videos, audio) with authentication
  const loadAuthenticatedMedia = async (url) => {
    try {
      setLoading(true);
      console.log('📎 Loading authenticated media from:', url);
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        console.error('Failed to load media:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setBlobUrl(objectUrl);
      setLoading(false);
      console.log('📎 Media loaded successfully, blob URL created');
    } catch (err) {
      console.error('Error loading authenticated media:', err);
      setError(true);
      setLoading(false);
    }
  };

  // Cleanup blob URL on unmount or when attachment changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Function to load and convert DOCX
  const loadDocx = async (url) => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(url);
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value);
      setLoading(false);
    } catch (err) {
      console.error('Error loading DOCX:', err);
      setError(true);
      setLoading(false);
    }
  };

  // Function to load and convert XLSX
  const loadXlsx = async (url) => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(url);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Convert all sheets to HTML
      let html = '';
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        html += `<h3 style="margin: 20px 0 10px 0; color: #374151; font-size: 18px; font-weight: 600;">${sheetName}</h3>`;
        html += XLSX.utils.sheet_to_html(worksheet, { 
          id: sheetName,
          editable: false 
        });
      });
      
      setXlsxHtml(html);
      setLoading(false);
    } catch (err) {
      console.error('Error loading XLSX:', err);
      setError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!attachment) return;
    
    // Reset states
    setDocxHtml('');
    setXlsxHtml('');
    setError(false);
    setLoading(false);
    setBlobUrl(null); // Reset blob URL
    
    const fileName = attachment.name || attachment.filename || '';
    const fileUrl = attachment.url || '';
    const contentType = attachment.type || attachment.content_type || '';
    
    // Debug logging
    console.log('AttachmentPreview - File details:', { fileName, fileUrl, contentType, attachment });
    
    // Determine preview type based on file extension or content type
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const mimeType = contentType.toLowerCase();
    
    // Construct proxy URL for task/comment attachments
    const API = process.env.REACT_APP_BACKEND_URL || '';
    let proxyUrl = fileUrl;
    
    // Check if this is a task attachment needing proxy
    const needsAuthProxy = attachment.id && (attachment.task_id || attachment.comment_id);
    
    if (attachment.id && attachment.task_id) {
      proxyUrl = `${API}/api/tasks/${attachment.task_id}/attachments/${attachment.id}/download`;
    } else if (attachment.id && attachment.comment_id) {
      proxyUrl = `${API}/api/comments/${attachment.comment_id}/attachments/${attachment.id}/proxy`;
    }
    
    if (
      mimeType.startsWith('image/') || 
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(extension)
    ) {
      setPreviewType('image');
      // Load image with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      mimeType === 'application/pdf' || 
      extension === 'pdf'
    ) {
      setPreviewType('pdf');
      // Load PDF with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      mimeType.startsWith('video/') || 
      ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(extension)
    ) {
      setPreviewType('video');
      // Load video with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      mimeType.startsWith('audio/') || 
      ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(extension)
    ) {
      setPreviewType('audio');
      // Load audio with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      mimeType.startsWith('text/') || 
      ['txt', 'json', 'xml', 'csv', 'md', 'log', 'js', 'py', 'java', 'c', 'cpp', 'h', 'css', 'html'].includes(extension)
    ) {
      setPreviewType('text');
      // Load text with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      extension === 'docx' ||
      mimeType.includes('wordprocessingml')
    ) {
      setPreviewType('docx');
      loadDocx(proxyUrl);
    } else if (
      ['xls', 'xlsx'].includes(extension) ||
      mimeType.includes('spreadsheetml') ||
      mimeType.includes('ms-excel')
    ) {
      setPreviewType('xlsx');
      loadXlsx(proxyUrl);
    } else if (
      ['doc', 'ppt', 'pptx'].includes(extension) ||
      mimeType.includes('msword') ||
      mimeType.includes('ms-powerpoint') ||
      mimeType.includes('presentation')
    ) {
      setPreviewType('office-unsupported');
    } else if (
      extension === 'csv' ||
      mimeType === 'text/csv' ||
      mimeType === 'application/csv'
    ) {
      setPreviewType('csv');
      // Load CSV with auth if it's a protected resource
      if (needsAuthProxy) {
        loadAuthenticatedMedia(proxyUrl);
      }
    } else if (
      ['zip', 'rar', '7z', 'tar', 'gz', 'ai', 'eps', 'psd', 'sketch', 'fig'].includes(extension) ||
      mimeType.includes('zip') ||
      mimeType.includes('compressed') ||
      mimeType.includes('archive')
    ) {
      // Files that should not preview - just download
      setPreviewType('download-only');
    } else {
      setPreviewType('not-supported');
    }
    
    setError(false);
  }, [attachment]);

  if (!isOpen || !attachment) return null;

  const fileName = attachment.name || attachment.filename || 'Unknown File';
  
  // Construct proxy URL for task attachments (to get fresh signed URLs)
  const API = process.env.REACT_APP_BACKEND_URL || '';
  let fileUrl = attachment.url || attachment.file_url || '';
  
  console.log('📎 AttachmentPreview - Initial data:', { 
    attachment, 
    has_id: !!attachment.id, 
    has_task_id: !!attachment.task_id,
    initial_url: fileUrl 
  });
  
  // If this is a task attachment with id and task_id, use proxy endpoint
  if (attachment.id && attachment.task_id) {
    fileUrl = `${API}/api/tasks/${attachment.task_id}/attachments/${attachment.id}/download`;
    console.log('📎 Using task proxy URL:', fileUrl);
  }
  // If this is a comment attachment, use comment proxy
  else if (attachment.id && attachment.comment_id) {
    fileUrl = `${API}/api/comments/${attachment.comment_id}/attachments/${attachment.id}/proxy`;
    console.log('📎 Using comment proxy URL:', fileUrl);
  }
  // Otherwise use the direct URL (for portal attachments or other types)
  else {
    console.log('📎 Using direct URL:', fileUrl);
  }
  
  const fileSize = attachment.size || attachment.file_size ? `${((attachment.size || attachment.file_size) / 1024).toFixed(2)} KB` : '';

  const handleDownload = async () => {
    // Use the proxy URL (already constructed above) or fallback to generic download
    const API = process.env.REACT_APP_BACKEND_URL || '';
    let downloadUrl = fileUrl;

    // If using direct URL (not proxy), use generic download endpoint
    if (!fileUrl.includes('/tasks/') && !fileUrl.includes('/comments/')) {
      downloadUrl = `${API}/api/download-attachment?file_url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(fileName)}`;
    }

    // Use fetch + blob so the Authorization header is included.
    // Opening the URL directly in a new tab sends no headers and gets a 401.
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        console.error('Download failed:', response.status, await response.text());
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Release the object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const getFileIcon = () => {
    switch (previewType) {
      case 'image':
        return <ImageIcon className="w-16 h-16 text-purple-500" />;
      case 'pdf':
        return <FileText className="w-16 h-16 text-red-500" />;
      case 'video':
        return <Film className="w-16 h-16 text-blue-500" />;
      case 'audio':
        return <Music className="w-16 h-16 text-green-500" />;
      case 'text':
        return <FileText className="w-16 h-16 text-gray-500" />;
      default:
        return <File className="w-16 h-16 text-gray-400" />;
    }
  };

  const handleBackdropClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[200]" 
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{fileName}</h3>
            {fileSize && <p className="text-sm text-gray-500">{fileSize}</p>}
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownload();
              }}
              className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {error ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Preview</h4>
              <p className="text-gray-600 mb-2">There was an error loading this file.</p>
              <p className="text-xs text-gray-500 mb-4 max-w-md break-all">
                File: {fileName}<br />
                URL: {fileUrl?.substring(0, 100)}{fileUrl?.length > 100 ? '...' : ''}
              </p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download File</span>
              </button>
            </div>
          ) : previewType === 'image' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading image...</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <img
                  src={blobUrl || fileUrl}
                  alt={fileName}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  onError={() => setError(true)}
                />
              </div>
            )
          ) : previewType === 'pdf' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading PDF...</p>
                </div>
              </div>
            ) : (
              <iframe
                src={blobUrl || fileUrl}
                className="w-full h-[70vh] rounded-lg border-0"
                title={fileName}
                onError={() => setError(true)}
              />
            )
          ) : previewType === 'video' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading video...</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <video
                  src={blobUrl || fileUrl}
                  controls
                  className="max-w-full max-h-[70vh] rounded-lg"
                  onError={() => setError(true)}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )
          ) : previewType === 'audio' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading audio...</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
                <Music className="w-24 h-24 text-purple-500" />
                <audio
                  src={blobUrl || fileUrl}
                  controls
                  className="w-full max-w-md"
                  onError={() => setError(true)}
                >
                  Your browser does not support the audio tag.
                </audio>
                <p className="text-gray-600">{fileName}</p>
              </div>
            )
          ) : previewType === 'text' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading file...</p>
                </div>
              </div>
            ) : (
              <iframe
              src={blobUrl || fileUrl}
              className="w-full h-[70vh] rounded-lg border border-gray-300 bg-white"
              title={fileName}
              onError={() => setError(true)}
            />
            )
          ) : previewType === 'docx' ? (
            loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading document...</p>
                </div>
              </div>
            ) : (
              <div className="w-full h-[70vh] overflow-auto p-6 bg-white">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: docxHtml }}
                  style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    lineHeight: '1.6',
                    color: '#374151'
                  }}
                />
              </div>
            )
          ) : previewType === 'xlsx' ? (
            loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading spreadsheet...</p>
                </div>
              </div>
            ) : (
              <div className="w-full h-[70vh] overflow-auto p-6 bg-gray-50">
                <div 
                  dangerouslySetInnerHTML={{ __html: xlsxHtml }}
                  style={{
                    '--table-border': '1px solid #e5e7eb'
                  }}
                />
                <style>{`
                  table {
                    border-collapse: collapse;
                    width: 100%;
                    margin-bottom: 20px;
                    background: white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                  }
                  table td, table th {
                    border: 1px solid #e5e7eb;
                    padding: 8px 12px;
                    text-align: left;
                    font-size: 14px;
                  }
                  table th {
                    background-color: #f3f4f6;
                    font-weight: 600;
                    color: #374151;
                  }
                  table tr:hover {
                    background-color: #f9fafb;
                  }
                `}</style>
              </div>
            )
          ) : previewType === 'office-unsupported' ? (
            <div className="w-full h-[70vh] flex flex-col items-center justify-center bg-gray-50">
              <div className="bg-white p-8 rounded-lg shadow-sm max-w-md text-center">
                <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Office Document</h4>
                <p className="text-sm text-gray-600 mb-4">{fileName}</p>
                <p className="text-xs text-gray-500 mb-6">This file type (DOC, PPT) requires download to view properly.</p>
                <button
                  onClick={handleDownload}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download File</span>
                </button>
              </div>
            </div>
          ) : previewType === 'csv' ? (
            loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading CSV...</p>
                </div>
              </div>
            ) : (
              <iframe
                src={blobUrl || fileUrl}
                className="w-full h-[70vh] rounded-lg border border-gray-300 bg-white"
                title={fileName}
                onError={() => setError(true)}
              />
            )
          ) : previewType === 'download-only' ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              {getFileIcon()}
              <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">Preview Not Available</h4>
              <p className="text-gray-600 mb-1">This file type cannot be previewed.</p>
              <p className="text-sm text-gray-500 mb-6">Click the download button to save and open the file.</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download File</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              {getFileIcon()}
              <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">Preview Not Available</h4>
              <p className="text-gray-600 mb-1">This file type cannot be previewed in the browser.</p>
              <p className="text-sm text-gray-500 mb-6">Download and view in a compatible program.</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download File</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render outside the DOM hierarchy to prevent event conflicts
  return ReactDOM.createPortal(modalContent, document.body);
};

export default AttachmentPreview;
